/**
 * The one service-worker registration, and the two things the app does with it.
 *
 * # Why this module exists
 *
 * `UpdatePrompt` owns the registration (it calls `useRegisterSW`), but it is
 * not the only thing that needs it: Settings has a "Check for updates" button,
 * and a periodic check runs whether or not a toast is on screen. Calling
 * `useRegisterSW` a second time would register a second time, so instead the
 * prompt publishes its registration here and everything else reads it.
 *
 * # Why a periodic check at all
 *
 * A browser looks for a new service worker on navigation. An installed PWA
 * that is never navigated -- the desktop wrapper, a phone's home-screen app,
 * a tab left open for a week -- therefore never looks, and keeps serving the
 * build it precached on the day it was installed. That is not a theory: the
 * owner reported mail lists that "have to reload randomly", and a panel
 * deployed minutes earlier was invisible in a browser that had the app open,
 * because the page was being served old chunks by an old worker.
 *
 * So the app asks on a timer, and when a window that has been in the
 * background comes back to the front. Both are cheap: `registration.update()`
 * is one conditional request for the worker script, and the browser only
 * installs anything if the bytes changed.
 */

export type UpdateCheck =
  /** A new version is downloaded and waiting; reloading will take it. */
  | 'ready'
  /** Asked, and this is already the newest build. */
  | 'current'
  /** No service worker here -- an unsupported browser, or a dev server. */
  | 'unsupported'
  /** The check itself failed, usually because the network is down. */
  | 'failed';

export interface UpdateState {
  /** When the last successful check finished, as epoch ms. */
  lastCheckedAt?: number;
  /** What that check said. */
  lastResult?: UpdateCheck;
  /** True while a check is in flight. */
  checking: boolean;
  /** True once a new worker is waiting to take over. */
  updateReady: boolean;
  /**
   * Another window switched this origin to a new version while this one was
   * busy: the page is running the old build under the new worker, and says
   * so until it is reloaded.
   */
  staleUnderNewWorker: boolean;
  /**
   * A handover is under way and will end in a reload: `user` after Reload was
   * pressed, `launch` for a silent one (at launch, or following another
   * window) -- no prompt is shown for those.
   */
  applying?: 'user' | 'launch';
}

import { isAppIdle } from './busy';
import {
  checkForUpdateLoop,
  resetHandoverDiagnosticsForTests,
  startHandoverTrace,
  type HandoverOutcome,
} from './handoverDiagnostics';

type Listener = () => void;

let registration: ServiceWorkerRegistration | undefined;
const INITIAL_STATE: UpdateState = { checking: false, updateReady: false, staleUnderNewWorker: false };
let state: UpdateState = INITIAL_STATE;
const listeners = new Set<Listener>();

function publish(next: Partial<UpdateState>): void {
  state = { ...state, ...next };
  for (const listener of listeners) listener();
}

/** `useSyncExternalStore` plumbing. */
export function subscribeToUpdates(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getUpdateState(): UpdateState {
  return state;
}

/**
 * Called by `UpdatePrompt` once the worker registers, and again whenever
 * vite-plugin-pwa reports that one is waiting.
 */
export function publishRegistration(reg: ServiceWorkerRegistration | undefined): void {
  registration = reg;
  // A worker can already be waiting at the moment we register -- the tab was
  // opened while an older one was still controlling it -- so this is not only
  // set by a later check.
  if (reg?.waiting != null) {
    publish({ updateReady: true });
    // Before the silent update below can reload again: if a Reload press
    // just led here, the update did not take, and that is worth a report.
    checkForUpdateLoop(reg, serviceWorkerContainer());
    maybeApplyAtLaunch();
  }
}

export function noteUpdateReady(): void {
  publish({ updateReady: true });
  checkForUpdateLoop(registration, serviceWorkerContainer());
}

/** Dismiss on the prompt shown after another window took the update. */
export function dismissStaleNotice(): void {
  if (state.staleUnderNewWorker) publish({ staleUnderNewWorker: false });
}

/**
 * Ask the browser whether there is a newer build.
 *
 * `registration.update()` resolves once the *request* is done, not once a new
 * worker has finished installing, so a plain `await` would report "up to date"
 * while a new version was still downloading. Hence the wait on `installing`.
 */
export async function checkForUpdate(): Promise<UpdateCheck> {
  if (registration === undefined) {
    publish({ checking: false, lastResult: 'unsupported', lastCheckedAt: Date.now() });
    return 'unsupported';
  }

  publish({ checking: true });
  try {
    await registration.update();
  } catch {
    publish({ checking: false, lastResult: 'failed', lastCheckedAt: Date.now() });
    return 'failed';
  }

  if (registration.waiting != null) return settle('ready');

  const installing = registration.installing;
  if (installing != null) {
    const outcome = await new Promise<UpdateCheck>((resolve) => {
      const onChange = (): void => {
        // `installed` means it is waiting for the running page to let go.
        // `activated` means it took over immediately, which happens when
        // nothing was controlling this page -- the new build is already what
        // this page would get, so there is nothing to announce.
        if (installing.state === 'installed') {
          installing.removeEventListener('statechange', onChange);
          resolve(registration?.active == null ? 'current' : 'ready');
        } else if (installing.state === 'activated' || installing.state === 'redundant') {
          installing.removeEventListener('statechange', onChange);
          resolve('current');
        }
      };
      installing.addEventListener('statechange', onChange);
    });
    return settle(outcome);
  }

  return settle('current');
}

function settle(result: UpdateCheck): UpdateCheck {
  publish({
    checking: false,
    lastResult: result,
    lastCheckedAt: Date.now(),
    updateReady: result === 'ready' ? true : state.updateReady,
  });
  return result;
}

/**
 * Take the waiting version: hand over, then reload -- once, and only once the
 * new worker is in charge.
 *
 * It used to reload on a fixed 2 s timer after posting SKIP_WAITING. On a
 * phone that is not long enough: the waiting worker has to be started, handle
 * the message and run its `activate` work (`cleanupOutdatedCaches`, the push
 * handlers' own). A reload that lands before that is served by the OLD worker,
 * the new one is still waiting, and the prompt comes straight back -- the
 * owner's "continually prompted to reload the app on mobile".
 *
 * So it reloads on whichever comes first:
 *
 * - `controllerchange`: this page's controller is now the new worker. The
 *   browser fires it as activation starts and holds the reload's navigation
 *   until activation has finished, so reloading here is already safe.
 * - the worker's own `statechange` to `activated`: the only signal an
 *   UNCONTROLLED page gets (a hard reload, or the first load after
 *   registration), where `controllerchange` never fires.
 * - {@link HANDOVER_TIMEOUT_MS}: neither arrived. Reloading is the whole point
 *   of the button, so it happens anyway; if the worker is somehow still
 *   waiting, the next page offers it again. This case is reported first
 *   (`pwa_handover_timeout`, ./handoverDiagnostics), and so is a next page
 *   that finds the worker still waiting (`pwa_update_loop`): the owner's
 *   installed app once looped here and left no trace of why.
 *
 * A waiting worker that turns `redundant` has been replaced by a newer one
 * (another deploy landed meanwhile): the handover follows the new one.
 */
export const HANDOVER_TIMEOUT_MS = 10_000;

const defaultReload = (): void => window.location.reload();

let handover: { cleanup: () => void } | undefined;
let reloaded = false;

function reloadOnce(reload: () => void): void {
  if (reloaded) return;
  reloaded = true;
  handover?.cleanup();
  reload();
}

function serviceWorkerContainer(): ServiceWorkerContainer | undefined {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator
    ? navigator.serviceWorker
    : undefined;
}

/** Returns false if posting threw (reported by the handover diagnostics). */
function skipWaiting(worker: ServiceWorker): boolean {
  try {
    worker.postMessage({ type: 'SKIP_WAITING' });
    return true;
  } catch {
    // A worker that cannot be messaged is on its way to `redundant`, which
    // the handover already follows.
    return false;
  }
}

export function applyUpdateAndReload(
  reload: () => void = defaultReload,
  why: 'user' | 'launch' = 'user',
): void {
  if (handover !== undefined || reloaded) return;
  publish({ applying: why });

  let target: ServiceWorker | null = registration?.waiting ?? null;
  if (target === null) {
    // Nothing waiting: another window already took it (this page is then
    // running the old build under the new worker), or there is no worker at
    // all. Either way a reload is the whole answer.
    reloadOnce(reload);
    return;
  }

  const container = serviceWorkerContainer();
  // Diagnostics only (./handoverDiagnostics): records what this handover sees
  // and, if it ends on the fallback timer, reports it before the reload.
  const trace = startHandoverTrace(target, why);
  const done = (outcome: HandoverOutcome): void => {
    if (!reloaded) trace.finish(outcome, registration, container);
    reloadOnce(reload);
  };
  const onControllerChange = (): void => done('controllerchange');
  const onStateChange = (): void => {
    if (target === null) return;
    trace.noteState(target);
    if (target.state === 'activated') done('activated');
    else if (target.state === 'redundant') follow(registration?.waiting ?? null);
  };
  const follow = (next: ServiceWorker | null): void => {
    target?.removeEventListener('statechange', onStateChange);
    if (next === null || next === target) {
      // Replaced by nothing we can see, or failed: nothing left to wait for.
      done('redundant');
      return;
    }
    target = next;
    trace.noteReplacement(target);
    target.addEventListener('statechange', onStateChange);
    trace.noteSkipWaiting(skipWaiting(target));
  };

  const timer = window.setTimeout(() => done('timeout'), HANDOVER_TIMEOUT_MS);
  container?.addEventListener('controllerchange', onControllerChange);
  handover = {
    cleanup: () => {
      window.clearTimeout(timer);
      container?.removeEventListener('controllerchange', onControllerChange);
      target?.removeEventListener('statechange', onStateChange);
    },
  };

  target.addEventListener('statechange', onStateChange);
  trace.noteSkipWaiting(skipWaiting(target));
  // It may have got there before anyone was listening.
  onStateChange();
}

/**
 * Another window took the update.
 *
 * Every window of the origin shares one worker, so when any of them applies
 * the update the rest get `controllerchange` too and are left running the old
 * build's code under the new worker, whose cache no longer has the old
 * build's lazy chunks. Idle, such a window just reloads, quietly. Busy (a
 * message being written, unsaved settings), it keeps the prompt up instead --
 * its Reload is then immediate, there being nothing left to wait for.
 *
 * Returns a teardown. Ignores the handover this window started itself, and a
 * page that was not controlled before (it is being claimed, not replaced).
 */
export function startExternalHandoverWatch(reload: () => void = defaultReload): () => void {
  const container = serviceWorkerContainer();
  if (container === undefined) return () => undefined;
  let hadController = container.controller != null;
  const onChange = (): void => {
    const had = hadController;
    hadController = container.controller != null;
    if (handover !== undefined || reloaded || !had) return;
    if (isAppIdle()) {
      publish({ applying: 'launch' });
      reloadOnce(reload);
    } else {
      publish({ staleUnderNewWorker: true, updateReady: true });
    }
  };
  container.addEventListener('controllerchange', onChange);
  return () => container.removeEventListener('controllerchange', onChange);
}

/**
 * The update at launch.
 *
 * A native app that finds an update as it starts simply starts on the new
 * version. So does this one: when the app has just been opened -- within
 * {@link LAUNCH_WINDOW_MS} of the page loading, before the first tap or key --
 * and a new version is already waiting or finishes installing in that time,
 * and nothing is held (`./busy`: no composer, no unsaved edits, no send in
 * its Undo window, no Outbox flush), it is applied silently: skip waiting,
 * reload once, no prompt. Anything later, or while busy, gets the prompt.
 *
 * Loop guard: at most one silent update per {@link LAUNCH_COOLDOWN_MS} per
 * tab, recorded in sessionStorage BEFORE reloading, so a worker that somehow
 * never takes over costs one reload and then shows the ordinary prompt.
 * Without working sessionStorage there is no silent update at all.
 */
export const LAUNCH_WINDOW_MS = 15_000;
export const LAUNCH_COOLDOWN_MS = 10 * 60 * 1000;
export const LAUNCH_FLAG_KEY = 'ols:pwa-launch-update';

let interacted = false;
let interactionTracked = false;

/** Call once, early (main.tsx): the first tap or key ends "just launched". */
export function trackLaunchInteraction(): void {
  if (interactionTracked || typeof window === 'undefined') return;
  interactionTracked = true;
  const mark = (): void => {
    interacted = true;
  };
  window.addEventListener('pointerdown', mark, { capture: true, once: true });
  window.addEventListener('keydown', mark, { capture: true, once: true });
}

/** Test seam: behave as if the person has tapped something. */
export function noteInteractionForTests(): void {
  interacted = true;
}

function sinceLoadMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Number.POSITIVE_INFINITY;
}

/** Records the silent update; false if one happened recently or storage is unusable. */
function claimLaunchUpdate(now: number): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(LAUNCH_FLAG_KEY));
    if (Number.isFinite(last) && last > 0 && now - last < LAUNCH_COOLDOWN_MS) return false;
    window.sessionStorage.setItem(LAUNCH_FLAG_KEY, String(now));
    return window.sessionStorage.getItem(LAUNCH_FLAG_KEY) === String(now);
  } catch {
    return false;
  }
}

/**
 * Apply a waiting version silently if the app has only just launched and
 * nothing is at stake. Returns true when it did (the page is about to reload
 * and no prompt should show). Safe to call repeatedly.
 */
export function maybeApplyAtLaunch(reload: () => void = defaultReload): boolean {
  if (state.applying === 'launch') return true;
  if (handover !== undefined || reloaded) return false;
  if (registration?.waiting == null) return false;
  if (interacted || sinceLoadMs() > LAUNCH_WINDOW_MS) return false;
  if (!isAppIdle()) return false;
  if (!claimLaunchUpdate(Date.now())) return false;
  applyUpdateAndReload(reload, 'launch');
  return true;
}

/**
 * How often an open window asks, and how long it must have been in the
 * background before coming back to the front counts as a reason to ask.
 *
 * An hour is frequent enough that a deploy reaches an open app the same
 * working day, and rare enough to be invisible in a request log.
 */
export const UPDATE_INTERVAL_MS = 60 * 60 * 1000;
export const FOREGROUND_RECHECK_MS = 15 * 60 * 1000;

/**
 * Start the periodic check. Returns a teardown.
 *
 * Checks are skipped while the document is hidden: a background tab that
 * cannot show the toast has no use for the answer, and browsers throttle its
 * timers anyway.
 */
export function startUpdateChecks(): () => void {
  let last = Date.now();

  const run = (): void => {
    if (document.visibilityState !== 'visible') return;
    last = Date.now();
    void checkForUpdate();
  };

  const timer = window.setInterval(run, UPDATE_INTERVAL_MS);

  const onVisible = (): void => {
    if (document.visibilityState !== 'visible') return;
    if (Date.now() - last < FOREGROUND_RECHECK_MS) return;
    run();
  };
  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', onVisible);

  return () => {
    window.clearInterval(timer);
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('focus', onVisible);
  };
}

/** Test seam: forget the registration and the recorded state. */
export function resetUpdateStateForTests(): void {
  registration = undefined;
  state = INITIAL_STATE;
  listeners.clear();
  handover?.cleanup();
  handover = undefined;
  reloaded = false;
  interacted = false;
  resetHandoverDiagnosticsForTests();
}
